import React from 'react';
import { SxProps, Theme } from '@mui/material';

import { FeaturePatternTrussSettings } from '@paragon/contract-library';
import { distanceGreaterThanOrEqual } from '@paragon/geometry-library';
import { DistanceField } from '@paragon/material';
import { enqueueError } from '@paragon/notification-tools';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { getProfileIsClosed } from '../../../../../../library/profiles/profileManipulation';
import { updateActiveFeaturePatternTrussSettings } from '../../../../../../store/actions/editorActions';
import { updateInsertingProfileTrussSettings } from '../../../../../../store/actions/profileActions';
import { focusCameraOnInsertionProfile } from '../../../../../../three/ThreeJsSceneActions';
import { useAppDispatch, useAppSelector } from '../../../../../hooks';

const textFieldSx = { m: 1, flex: 1 } as const;

export function Length(props: { sx?: SxProps<Theme> }) {
  const dispatch = useAppDispatch();

  const insertingObjectInfo = useAppSelector((state) => state.application.insertingObjectInfo);

  if (insertingObjectInfo?.type !== 'componentDesign') throw new Error('Expected to be inserting a component');

  const settings = insertingObjectInfo.designSettings;

  const onChangeFeaturePatternSetting = <K extends keyof FeaturePatternTrussSettings>(
    featurePatternSettings: FeaturePatternTrussSettings,
    key: K,
    value: FeaturePatternTrussSettings[K],
  ) => {
    if (featurePatternSettings[key] !== value) {
      dispatch(updateActiveFeaturePatternTrussSettings({ ...featurePatternSettings, [key]: value }));
    }
  };

  const currentLength =
    settings.definitionType === 'FeaturePattern'
      ? settings.width
      : settings.definitionType === 'Profile'
        ? settings.span
        : assertExhaustiveSwitch(settings);

  return (
    <DistanceField
      label="Length"
      value={currentLength}
      min={minTrussLength}
      onSubmit={async (value) => {
        if (exceedsMaxLength(value)) {
          dispatch(createExceedsMaxLengthErrorAction());
          return;
        }

        if (settings.definitionType === 'FeaturePattern') {
          await onChangeFeaturePatternSetting(settings, 'width', value);
        } else if (settings.definitionType === 'Profile') {
          const xScaleFactor = value / currentLength;
          const updatedProfileJoints = settings.profile.profileJoints.map((joint) => ({
            ...joint,
            location: { ...joint.location, x: joint.location.x * xScaleFactor },
          }));
          const updatedProfileLiens = settings.profile.profileLines.map((line) => ({
            ...line,
            fixedJoints: line.fixedJoints.map((joint) => ({ ...joint, x: joint.x * xScaleFactor })),
            featureJoints: line.featureJoints.map((joint) => ({ ...joint, x: joint.x * xScaleFactor })),
            webJointSegments: line.webJointSegments.map((segment) => ({
              ...segment,
              floatingJoints: segment.floatingJoints.map((joint) => ({ ...joint, x: joint.x * xScaleFactor })),
            })),
          }));

          const updatedProfile = {
            ...settings.profile,
            profileJoints: updatedProfileJoints,
            profileLines: updatedProfileLiens,
          };

          await dispatch(
            updateInsertingProfileTrussSettings({
              ...settings,
              profile: updatedProfile,
              span: value,
            }),
          );

          if (!getProfileIsClosed(updatedProfile)) {
            focusCameraOnInsertionProfile();
          }
        } else {
          assertExhaustiveSwitch(settings);
        }
      }}
      sx={[textFieldSx, ...(Array.isArray(props.sx) ? props.sx : [props.sx])]}
    />
  );
}

export function exceedsMaxLength(value: number) {
  return distanceGreaterThanOrEqual(value, 80 * 12);
}

export function createExceedsMaxLengthErrorAction() {
  return enqueueError(
    'Truss length must be less than 80 feet. Upgrade to a higher tier in order to create longer trusses.',
  );
}

/**
 * New users often put in trusses that are not long enough due to confusion about how our distance format works.
 * Therefore, we enforce a minimum truss length to avoid the confusion. 7" is somewhat arbitrarily chosen, but it is
 * based on the combined width of 2 2x4s.
 */
export const minTrussLength = 7;
